import {
  AlertCircle,
  Beaker,
  Brain,
  CheckCircle2,
  Clock,
  Database,
  Globe,
  History,
  Lightbulb,
  Loader2,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import type * as React from "react";

import { Trans, useLingui } from "@lingui/react/macro";

import { Badge, cn } from "@ando/ui";

import type { HypermemConsolidationJob, HypermemState } from "@ando/core";

import { useHypermemJobs, useHypermemState } from "@/features/admin/hooks/useAdminHypermem";
import { formatInt, formatMs, formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmError, HmLoading } from "./HmStates";

/**
 * HmOverviewTab — v2 port of ando-frontend's ``HypermemOverviewTab`` (Wave 7).
 *
 * Privacy-first admin overview of the HyperMem system: aggregate counters,
 * pillar health, last consolidation run + a feed of recent consolidation
 * jobs. NO PII. ``tenantId === null`` renders the cross-tenant platform-wide
 * aggregate (the BE endpoint supports both scopes).
 *
 * DRY: TanStack Query hooks (``useHypermemState`` / ``useHypermemJobs``),
 * shared IT-locale formatters and the ``AdminKpiGrid`` rail — matching the
 * loading / error / empty conventions of the other admin v2 tabs.
 */

const STALE_HOURS = 24;
const PILLAR_TOTAL = 7;

function isStale(iso: string | null | undefined): boolean {
  if (!iso) return true;
  const ageH = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  return ageH > STALE_HOURS;
}

/**
 * How many of the 7 HyperMem pillars are reporting non-zero activity.
 * Heuristic computed client-side from the aggregate counters (mirrors v1).
 */
function activePillars(state: HypermemState | undefined): number {
  if (!state) return 0;
  let n = 0;
  if (state.episodic_facts_count > 0) n++;
  if (state.semantic_facts_count > 0) n++;
  if (state.salience_scores_count > 0) n++;
  if (state.consolidation_jobs_24h > 0 || state.last_consolidation_run) n++;
  if (state.pending_workflow_suggestions > 0) n++;
  if (state.active_experiments > 0) n++;
  if (state.active_prompt_versions > 0) n++;
  return n;
}

// ── Inline helpers ──────────────────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: typeof Database;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 font-semibold text-ink-2 text-xs uppercase tracking-wide">
        {Icon ? <Icon className="h-3.5 w-3.5 text-ando-orange" aria-hidden /> : null}
        {title}
      </h3>
      {children}
    </section>
  );
}

function KpiCard({
  label,
  value,
  subValue,
  help,
}: {
  label: string;
  value: number | null | undefined;
  subValue?: string;
  help?: string;
}): React.ReactElement {
  return (
    <div className="rounded-xl border border-hairline bg-cream/40 p-4">
      <p className="font-medium text-[10px] text-ink-3 uppercase tracking-wide" title={help}>
        {label}
      </p>
      <p className="mt-1 font-display font-semibold text-[22px] text-ink-1 tabular-nums">
        {formatInt(value)}
      </p>
      {subValue ? <p className="mt-1 text-ink-3 text-xs">{subValue}</p> : null}
      {help ? <p className="mt-1 text-ink-3 text-xs leading-snug">{help}</p> : null}
    </div>
  );
}

function HealthCard({
  title,
  value,
  status = "neutral",
  icon: Icon,
}: {
  title: string;
  value: string | number;
  status?: "healthy" | "warning" | "neutral";
  icon?: typeof History;
}): React.ReactElement {
  const dot =
    status === "healthy" ? "bg-emerald-500" : status === "warning" ? "bg-amber-500" : "bg-ink-3/40";
  return (
    <div className="rounded-xl border border-hairline bg-cream/40 p-4">
      <div className="flex items-center justify-between">
        <span className="font-medium text-[10px] text-ink-3 uppercase tracking-wide">{title}</span>
        {Icon ? <Icon className="h-4 w-4 text-ando-orange" aria-hidden /> : null}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className={cn("h-2 w-2 rounded-full", dot)} aria-hidden />
        <span className="font-semibold text-ink-1 text-xl tabular-nums">{value}</span>
      </div>
    </div>
  );
}

const JOB_STATUS_META: Record<string, { icon: typeof CheckCircle2; tone: string }> = {
  completed: { icon: CheckCircle2, tone: "text-emerald-600" },
  success: { icon: CheckCircle2, tone: "text-emerald-600" },
  failed: { icon: XCircle, tone: "text-red-600" },
  error: { icon: XCircle, tone: "text-red-600" },
  running: { icon: Loader2, tone: "text-ando-orange" },
  pending: { icon: Clock, tone: "text-ink-3" },
};

function JobRow({
  job,
}: {
  job: HypermemConsolidationJob;
}): React.ReactElement {
  const meta = JOB_STATUS_META[job.status] ?? {
    icon: Clock,
    tone: "text-ink-3",
  };
  const StatusIcon = meta.icon;
  const spinning = job.status === "running";
  return (
    <li className="flex items-start justify-between gap-3 py-2.5 text-sm">
      <div className="flex min-w-0 items-start gap-2">
        <StatusIcon
          className={cn("mt-0.5 h-4 w-4 shrink-0", meta.tone, spinning && "animate-spin")}
          aria-hidden
        />
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-1" title={job.job_type}>
            {job.job_type}
          </p>
          <p className="text-ink-3 text-xs">
            {formatTimestamp(job.created_at)}
            {" · "}
            <Trans>{formatInt(job.items_processed)} item</Trans>
            {" · "}
            {formatMs(job.duration_ms)}
          </p>
          {job.error ? (
            <p className="mt-0.5 truncate text-red-600 text-xs" title={job.error}>
              {job.error}
            </p>
          ) : null}
        </div>
      </div>
      <Badge variant="outline" className={cn("shrink-0 text-xs", meta.tone)}>
        {job.status}
      </Badge>
    </li>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function HmOverviewTab({
  tenantId,
}: {
  tenantId: string | null;
}): React.ReactElement {
  const { t } = useLingui();
  const { data: state, isLoading: stateLoading, isError: stateError } = useHypermemState(tenantId);
  const {
    data: jobsData,
    isLoading: jobsLoading,
    isError: jobsError,
  } = useHypermemJobs(tenantId, 20);

  if (stateLoading && !state) {
    return <HmLoading label={<Trans>Caricamento stato HyperMem…</Trans>} />;
  }

  if (stateError && !state) {
    return <HmError message={<Trans>Impossibile caricare lo stato HyperMem.</Trans>} />;
  }

  const pillars = activePillars(state);
  const lastRun = state?.last_consolidation_run ?? null;
  const validPct =
    state && state.semantic_facts_count > 0
      ? Math.round((100 * state.valid_semantic_facts_count) / state.semantic_facts_count)
      : 0;
  const jobs: HypermemConsolidationJob[] = jobsData?.jobs ?? [];

  return (
    <div className="space-y-6">
      {/* Cross-tenant aggregate banner. */}
      {!tenantId && (
        <div className="flex items-center gap-2 rounded-xl border border-ando-orange/30 bg-ando-orange/10 p-3 text-ink-1 text-sm">
          <Globe className="h-4 w-4 shrink-0 text-ando-orange" aria-hidden />
          <span>
            <Trans>
              <strong>Platform Overview</strong> — somma cross-tenant di tutti i tenant del sistema
              (no PII).
            </Trans>
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2">
        <Brain className="h-4 w-4 text-ando-orange" aria-hidden />
        <h2 className="font-semibold text-base text-ink-1">
          <Trans>Stato HyperMem</Trans>
        </h2>
      </div>

      {/* Health row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <HealthCard
          title={t`Pillar attivi`}
          value={`${pillars}/${PILLAR_TOTAL}`}
          status={pillars >= 5 ? "healthy" : pillars >= 3 ? "warning" : "neutral"}
          icon={ShieldAlert}
        />
        <HealthCard
          title={t`Ultimo consolidation job`}
          value={lastRun ? formatTimestamp(lastRun) : t`Mai`}
          status={isStale(lastRun) ? "warning" : "healthy"}
          icon={History}
        />
        <HealthCard
          title={t`Esperimenti A/B attivi`}
          value={formatInt(state?.active_experiments)}
          icon={Beaker}
        />
      </div>

      {/* Memory counters */}
      <Section title={t`Memoria`} icon={Database}>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <KpiCard
            label={t`Episodic facts`}
            value={state?.episodic_facts_count}
            help={t`Eventi grezzi catturati nelle ultime sessioni`}
          />
          <KpiCard label={t`Semantic facts (totali)`} value={state?.semantic_facts_count} />
          <KpiCard
            label={t`Semantic facts (validi)`}
            value={state?.valid_semantic_facts_count}
            subValue={t`${validPct}% validi`}
          />
          <KpiCard
            label={t`Salience scores`}
            value={state?.salience_scores_count}
            help={t`Punteggi di rilevanza calcolati dal consolidation`}
          />
        </div>
      </Section>

      {/* Process codification */}
      <Section title={t`Process codification`} icon={Lightbulb}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <KpiCard
            label={t`Workflow suggestions in attesa`}
            value={state?.pending_workflow_suggestions}
            help={t`Pattern di azioni ricorrenti rilevati ma non ancora salvati come workflow`}
          />
        </div>
      </Section>

      {/* Activity */}
      <Section title={t`Attività ultima giornata`} icon={History}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <KpiCard label={t`Consolidation jobs (24h)`} value={state?.consolidation_jobs_24h} />
          <KpiCard label={t`Prompt versioni attive`} value={state?.active_prompt_versions} />
        </div>
      </Section>

      {/* Recent consolidation jobs feed */}
      <Section title={t`Consolidation jobs recenti`} icon={History}>
        <div className="rounded-xl border border-hairline bg-cream/40 p-4">
          {jobsLoading && jobs.length === 0 ? (
            <div className="flex items-center gap-2 py-6 text-ink-3">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              <span className="text-sm">
                <Trans>Caricamento job…</Trans>
              </span>
            </div>
          ) : jobsError ? (
            <div className="flex items-start gap-2 py-2 text-red-700 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                <Trans>Impossibile caricare i consolidation jobs.</Trans>
              </span>
            </div>
          ) : jobs.length === 0 ? (
            <p className="py-4 text-ink-3 text-sm">
              <Trans>Nessun consolidation job registrato.</Trans>
            </p>
          ) : (
            <ul className="divide-y divide-hairline">
              {jobs.map((job) => (
                <JobRow key={job.id} job={job} />
              ))}
            </ul>
          )}
        </div>
      </Section>

      {/* Footer note (privacy hint) */}
      <p className="flex flex-wrap items-center gap-2 border-hairline border-t pt-3 text-ink-3 text-xs">
        <Trans>🇪🇺 Dati ospitati in EU · Ruolo: aggregato (no PII)</Trans>
      </p>
    </div>
  );
}

export default HmOverviewTab;
